import React from "react";
import {
  Card,
  Text,
  ProgressBar,
  BlockStack,
  InlineStack,
  Box,
} from "@shopify/polaris";

/**
 * Active Conversions Component
 * Shows currently processing video conversions with progress
 */
const ActiveConversions = ({ conversions }) => {
  const getFirstNonNullValue = (obj) => {
    if (!obj) return "Untitled";
    for (const value of Object.values(obj)) {
      if (value !== null) return value;
    }
    return "Untitled";
  };

  const formatTimeAgo = (startedAt) => {
    if (!startedAt) return "Unknown";
    
    const now = new Date();
    const start = new Date(startedAt);
    const diffMinutes = Math.floor((now - start) / 1000 / 60);
    
    if (diffMinutes < 1) return "Just now";
    if (diffMinutes === 1) return "1 min ago";
    return `${diffMinutes} min ago`;
  };

  const estimateTimeRemaining = (progress) => {
    if (!progress || progress === 0) return "Calculating...";
    
    // Rough estimate: if 45% done in X minutes, 100% will take ~2.2X minutes
    // Average video takes ~6-8 minutes
    const avgMinutes = 7;
    const remainingPercent = 100 - progress;
    const estimated = Math.round((remainingPercent / 100) * avgMinutes);
    
    if (estimated < 1) return "Less than 1 min";
    if (estimated === 1) return "~1 min remaining";
    return `~${estimated} min remaining`;
  };

  return (
    <Card>
      <Box padding="4">
        <BlockStack gap="4">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingLg">
              ⚡ Active Conversions ({conversions.length})
            </Text>
          </InlineStack>

          <BlockStack gap="3">
            {conversions.map((conversion) => (
              <Box
                key={conversion.id}
                padding="4"
                background="bg-surface-secondary"
                borderRadius="200"
              >
                <BlockStack gap="3">
                  {/* Video Name */}
                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                    {getFirstNonNullValue(conversion.playlist?.name) || "Converting..."}
                  </Text>

                  {/* Progress Bar */}
                  <ProgressBar
                    progress={conversion.progress || 0}
                    size="medium"
                    tone="primary"
                  />

                  {/* Progress Info */}
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="p" variant="bodySm" tone="subdued">
                      Started: {formatTimeAgo(conversion.started_at)}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      {estimateTimeRemaining(conversion.progress)}
                    </Text>
                  </InlineStack>

                  {/* Status Details */}
                  <InlineStack gap="2">
                    <Text as="span" variant="bodySm" tone="subdued">
                      Status: {conversion.status}
                    </Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                      •
                    </Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                      Progress: {conversion.progress || 0}%
                    </Text>
                  </InlineStack>
                </BlockStack>
              </Box>
            ))}
          </BlockStack>
        </BlockStack>
      </Box>
    </Card>
  );
};

export default ActiveConversions;